import { useState, useEffect } from "react";
import { ShieldCheck, Smartphone, Loader2, UserCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export function VerifyIdentity({ user, onDone, open: openProp, onOpenChange, showBanner = true, triggerLabel = "Verifiser identitet" }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = onOpenChange || setOpenState;
  const [nid, setNid] = useState(user?.national_id || "");
  const [name, setName] = useState(user?.name || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [iduraOn, setIduraOn] = useState(false);

  useEffect(() => {
    api.get("/auth/idura/status").then((r) => setIduraOn(!!r.data?.configured)).catch(() => setIduraOn(false));
  }, []);

  if (!user || user.identity_verified) return null;
  const pending = user.verification_status === "pending";

  const startIdura = () => {
    window.location.assign(`${window.location.origin}/api/auth/idura/login`);
  };

  const doBankid = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify/bankid", { national_id: nid.replace(/\s/g, ""), name });
      toast.success("Identitet verifisert med BankID");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doAdmin = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify/request-admin", { national_id: nid.replace(/\s/g, ""), name, note });
      toast.success("Forespørsel sendt til admin");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {showBanner ? (
        <div
          data-testid="verify-identity-banner"
          className={`mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${
            pending ? "border-amber-500/25 bg-amber-500/10" : "border-primary/30 bg-primary/[0.06]"
          }`}
        >
          {pending ? (
            <span className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <Clock className="h-4 w-4" /> Identitetsverifisering venter på godkjenning fra admin.
            </span>
          ) : (
            <>
              <span className="flex items-center gap-2 font-medium">
                <ShieldCheck className="h-4 w-4 text-primary" /> Verifiser identiteten din for å kunne legge til selskap.
              </span>
              <Button size="sm" data-testid="verify-identity-btn" onClick={() => setOpen(true)}>{triggerLabel}</Button>
            </>
          )}
        </div>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="verify-dialog">
          <DialogHeader>
            <DialogTitle>Verifiser identitet</DialogTitle>
            <DialogDescription>Vi bruker fødselsnummer og navn til å hente rollene dine fra Proff.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Navn</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="verify-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label>Fødselsnummer (11 siffer)</Label>
              <Input value={nid} onChange={(e) => setNid(e.target.value)} inputMode="numeric" placeholder="11 siffer" data-testid="verify-nid-input" className="mt-1.5" />
            </div>
          </div>
          <Tabs defaultValue="bankid" className="mt-2">
            <TabsList className="w-full">
              <TabsTrigger value="bankid" data-testid="verify-tab-bankid" className="flex-1">BankID</TabsTrigger>
              <TabsTrigger value="admin" data-testid="verify-tab-admin" className="flex-1">Be admin verifisere</TabsTrigger>
            </TabsList>
            <TabsContent value="bankid" className="mt-3">
              {iduraOn ? (
                <>
                  <p className="text-xs text-muted-foreground">Du sendes til BankID for sikker verifisering. Navn og fødselsnummer hentes automatisk.</p>
                  <Button className="mt-3 w-full gap-2" onClick={startIdura} data-testid="verify-bankid-submit">
                    <Smartphone className="h-4 w-4" /> Verifiser med BankID
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">Simulert BankID – bekreft identiteten din umiddelbart.</p>
                  <Button className="mt-3 w-full gap-2" onClick={doBankid} disabled={busy} data-testid="verify-bankid-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} Verifiser med BankID
                  </Button>
                </>
              )}
            </TabsContent>
            <TabsContent value="admin" className="mt-3">
              <Label className="text-xs">Melding til admin (valgfritt)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="verify-note-input" placeholder="f.eks. hvorfor du trenger tilgang" />
              <Button className="mt-3 w-full gap-2" variant="outline" onClick={doAdmin} disabled={busy} data-testid="verify-admin-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} Be admin om verifisering
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
